"use client"

import React from "react"
import { useState } from "react"
import { Send, Bell, Users, Truck, Shield, Megaphone, Gift, AlertCircle } from "lucide-react"
import { Box, Flex, Card, Grid, Heading, Text, TextField, TextArea, Select, Button, Badge } from "@radix-ui/themes"
import { useNotificationService } from "../services/notificationService"
import { toast } from "react-hot-toast"
import PageHeader from "../components/ui/dashboard/PageHeader"

interface NotificationTemplate {
  id: string
  title: string
  body: string
  icon: React.ReactNode
  category: string
  customData?: any
}

const NotificationsPage: React.FC = () => {
  const [loading, setLoading] = useState(false)
  const [formData, setFormData] = useState({
    title: "",
    body: "",
    targetAudience: "all",
    customData: "{}",
  })

  const notificationService = useNotificationService()

  const templates: NotificationTemplate[] = [
    {
      id: "welcome",
      title: "Welcome to TurboDelivery!",
      body: "Start ordering your favorite food from local restaurants.",
      icon: <Bell className="w-5 h-5" />,
      category: "engagement",
    },
    {
      id: "promotion",
      title: "Special Offer - 20% Off!",
      body: "Use code SAVE20 on your next order. Limited time offer!",
      icon: <Gift className="w-5 h-5" />,
      category: "promotion",
      customData: { promoCode: "SAVE20", discount: 20 },
    },
    {
      id: "new_restaurant",
      title: "New Restaurant Added!",
      body: "Check out the latest addition to our restaurant family.",
      icon: <Megaphone className="w-5 h-5" />,
      category: "update",
    },
    {
      id: "captain_welcome",
      title: "Welcome Captain!",
      body: "Your application has been approved. Start earning today!",
      icon: <Truck className="w-5 h-5" />,
      category: "captain",
    },
    {
      id: "maintenance",
      title: "Scheduled Maintenance",
      body: "Our app will be under maintenance from 2 AM to 4 AM tonight.",
      icon: <AlertCircle className="w-5 h-5" />,
      category: "system",
    },
    {
      id: "security_update",
      title: "Security Update Available",
      body: "Please update your app to the latest version for enhanced security.",
      icon: <Shield className="w-5 h-5" />,
      category: "security",
    },
  ]

  const audienceOptions = [
    { value: "all", label: "All Users", icon: <Users className="w-4 h-4" /> },
    { value: "customers", label: "Customers Only", icon: <Users className="w-4 h-4" /> },
    { value: "captains", label: "Captains Only", icon: <Truck className="w-4 h-4" /> },
    { value: "admins", label: "Admins Only", icon: <Shield className="w-4 h-4" /> },
  ]

  const handleSendNotification = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.title.trim() || !formData.body.trim()) {
      toast.error("Please fill in both title and body")
      return
    }

    if (!notificationService.isWebSocketConnected()) {
      toast.error("Not connected to server. Please wait and try again.")
      return
    }

    try {
      setLoading(true)

      let customData = {}
      if (formData.customData.trim()) {
        try {
          customData = JSON.parse(formData.customData)
        } catch (error) {
          toast.error("Invalid JSON in custom data field")
          return
        }
      }

      const notificationData = {
        title: formData.title,
        message: formData.body,
        targetAudience: formData.targetAudience,
        customData,
      }

      // Send via Socket.IO instead of HTTP API
      notificationService.send('send_notification', notificationData)

      toast.success("Notification sent successfully!")
      setFormData({
        title: "",
        body: "",
        targetAudience: "all",
        customData: "{}",
      })
    } catch (error) {
      console.error("Error sending notification:", error)
      toast.error("Failed to send notification. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  const handleTemplateSelect = (template: NotificationTemplate) => {
    setFormData({
      title: template.title,
      body: template.body,
      targetAudience: template.category === "captain" ? "captains" : "all",
      customData: JSON.stringify(template.customData || {}, null, 2),
    })
  }

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  return (
    <Flex direction="column" gap="6">
      <PageHeader
        title="Push Notifications"
        description="Send custom notifications to your users"
        action={
          <Flex align="center" gap="2">
            <Bell className="w-4 h-4" />
            {notificationService.isWebSocketConnected() ? (
              <Badge color="green">Connected</Badge>
            ) : (
              <Badge color="red">Disconnected</Badge>
            )}
          </Flex>
        }
      />

      <Grid columns={{ initial: "1", lg: "3" }} gap="6">
        {/* Notification Form */}
        <Box style={{ gridColumn: "span 2" }}>
          <Card>
            <Flex direction="column" gap="4">
              <Heading size="4">Create Notification</Heading>

              <form onSubmit={handleSendNotification}>
                <Flex direction="column" gap="4">
                  <Box>
                    <Text as="label" size="2" weight="medium">Notification Title *</Text>
                    <TextField.Root
                      required
                      value={formData.title}
                      onChange={(e) => handleInputChange("title", e.target.value)}
                      placeholder="Enter notification title..."
                      maxLength={50}
                    />
                    <Text size="1" color="gray">{formData.title.length}/50 characters</Text>
                  </Box>

                  <Box>
                    <Text as="label" size="2" weight="medium">Notification Body *</Text>
                    <TextArea
                      required
                      value={formData.body}
                      onChange={(e) => handleInputChange("body", e.target.value)}
                      placeholder="Enter notification message..."
                      maxLength={200}
                      rows={3}
                    />
                    <Text size="1" color="gray">{formData.body.length}/200 characters</Text>
                  </Box>

                  <Box>
                    <Text as="label" size="2" weight="medium">Target Audience</Text>
                    <Select.Root value={formData.targetAudience} onValueChange={(value) => handleInputChange("targetAudience", value)}>
                      <Select.Trigger style={{ width: "100%" }} />
                      <Select.Content>
                        {audienceOptions.map((option) => (
                          <Select.Item key={option.value} value={option.value}>
                            {option.label}
                          </Select.Item>
                        ))}
                      </Select.Content>
                    </Select.Root>
                  </Box>

                  <Box>
                    <Text as="label" size="2" weight="medium">Custom Data (JSON)</Text>
                    <TextArea
                      value={formData.customData}
                      onChange={(e) => handleInputChange("customData", e.target.value)}
                      placeholder='{"key": "value"}'
                      rows={3}
                      style={{ fontFamily: "monospace" }}
                    />
                    <Text size="1" color="gray">Optional: Add custom data for app-specific actions</Text>
                  </Box>

                  <Button
                    type="submit"
                    disabled={loading || !notificationService.isWebSocketConnected()}
                    style={{ width: "100%" }}
                  >
                    {loading ? (
                      <>
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        Send Notification
                      </>
                    )}
                  </Button>
                </Flex>
              </form>
            </Flex>
          </Card>
        </Box>

        {/* Templates Sidebar */}
        <Flex direction="column" gap="6">
          <Card>
            <Flex direction="column" gap="4">
              <Heading size="4">Quick Templates</Heading>
              <Flex direction="column" gap="3">
                {templates.map((template) => (
                  <Button
                    key={template.id}
                    variant="outline"
                    onClick={() => handleTemplateSelect(template)}
                    style={{ width: "100%", textAlign: "left", justifyContent: "flex-start" }}
                  >
                    <Flex direction="column" gap="1" align="start">
                      <Flex align="center" gap="2">
                        {template.icon}
                        <Text size="2" weight="medium">
                          {template.title}
                        </Text>
                      </Flex>
                      <Text size="1" color="gray" style={{ overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
                        {template.body}
                      </Text>
                    </Flex>
                  </Button>
                ))}
              </Flex>
            </Flex>
          </Card>

          <Card>
            <Flex direction="column" gap="4">
              <Heading size="4">Notification Stats</Heading>
              <Flex direction="column" gap="3">
                <Flex justify="between">
                  <Text size="2" color="gray">Today</Text>
                  <Text size="2" weight="medium">12 sent</Text>
                </Flex>
                <Flex justify="between">
                  <Text size="2" color="gray">This Week</Text>
                  <Text size="2" weight="medium">89 sent</Text>
                </Flex>
                <Flex justify="between">
                  <Text size="2" color="gray">This Month</Text>
                  <Text size="2" weight="medium">342 sent</Text>
                </Flex>
                <Flex justify="between">
                  <Text size="2" color="gray">Success Rate</Text>
                  <Text size="2" weight="medium" color="green">98.5%</Text>
                </Flex>
              </Flex>
            </Flex>
          </Card>
        </Flex>
      </Grid>
    </Flex>
  )
}

export default NotificationsPage
